/**
 * Notification Setup — the tenant's per-event notification configuration.
 *
 * Replaces the old Notification Defaults tab, which wrote
 * `PlatformSettings.default_notification_preferences` — a field nothing in the
 * backend ever read. Every control here lands on a row the emit pipeline
 * resolves against on the next notification (scope doc §D).
 *
 * The grid is driven entirely by `GET /notifications/event-types/`: one row per
 * shipped event, grouped by module, with the tenant's override merged in by the
 * same resolver the emitter uses. Nothing about the taxonomy is hardcoded here,
 * so an event shipped in a later data migration appears with its defaults and
 * no override the next time this tab is opened.
 *
 * Two tabs under one header, so "Save changes" and the unsaved count are in
 * view from both: Events (the switches and the grid) and Delivery (everything
 * about how mail goes out). One `useNotificationSetup` lives here, and with it
 * the staged rows, so moving between tabs loses nothing.
 *
 * Save model: master switches and the Delivery cards apply immediately (see
 * NotificationMasterCard); per-event edits stage and go out on "Save changes".
 *
 * The header states which company the server resolved this configuration to,
 * and how (`meta.company_name` / `meta.company_scope_source`). It is the only
 * place the screen makes a claim about scope, and it used to make none — a
 * platform operator could read a division's name off the top bar while every
 * read and write landed on their own company's row. Under it, CR-9029: how that
 * same company's notifications are routed (`meta.baseline_routing`), Standard or
 * Owner first, with what each means. Support changes it by command, so no toggle.
 *
 * Known gap: the per-event save fans out one upsert per changed row rather than
 * using the single bulk round trip, because `notificationsAPI.bulkSaveOverrides`
 * currently targets `PUT /template-overrides/bulk/` with an `{overrides, …}`
 * body while the API exposes `POST /template-overrides/bulk_update/` taking
 * `{rows: […]}`. `POST /template-overrides/` is an upsert keyed on
 * `template_key` and matches field-for-field, so it is what this screen uses
 * until the two are reconciled in `lib/api/notifications.ts` (owned elsewhere).
 */
import { useEffect, useState } from 'react';
import { isCustomerSideUser } from '../../../../lib/adminSetupAccess';
import { useAuth } from '../../../../contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import {
  AlertCircle,
  Building2,
  Info,
  Loader2,
  RefreshCw,
  Route,
  Save,
  ShieldAlert,
} from 'lucide-react';
import type { PlatformSettings } from '../types';
import { NotificationDeliveryTab } from './NotificationDeliveryTab';
import { NotificationEventsTab } from './NotificationEventsTab';
import { useEventsView } from './useEventsView';
import { useNotificationSetup } from './useNotificationSetup';
import { useQuietHoursDraft } from './useQuietHoursDraft';
import {
  describeScope,
  ROUTING_CHANGED_BY,
  ROUTING_EXPLANATIONS,
  ROUTING_LABELS,
  tenantOneDaily,
} from './types';

interface NotificationSetupTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
  /**
   * Reports how many rows are staged but unsaved, so the screen hosting this
   * tab can guard an in-page section switch the same way `beforeunload` guards
   * leaving the page. Reports 0 on unmount, so a host never holds a stale
   * count for a tab that is no longer open.
   *
   * @link useUnsavedChangesGuard
   */
  onUnsavedChangesChange?: (count: number) => void;
}

export function NotificationSetupTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
  onUnsavedChangesChange,
}: NotificationSetupTabProps) {
  const setup = useNotificationSetup();
  // CR-8217: the master switches are install-wide, and the alert addresses
  // save through Admin Setup — both are refused to anyone but the customer side.
  const { user } = useAuth();
  const canSetInstallSwitches = isCustomerSideUser(user);
  // All three live above the error state below, which replaces everything
  // under it.
  const eventsView = useEventsView(setup.catalogue);
  const quietHours = useQuietHoursDraft(setup.master, setup.catalogue?.companyId ?? null);
  const [activeTab, setActiveTab] = useState('events');

  // Unsaved-changes guard: the grid can hold a lot of staged work.
  useEffect(() => {
    if (setup.dirtyCount === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [setup.dirtyCount]);

  // The other half of that guard. `beforeunload` only fires when the *page*
  // goes; Admin Setup switches section in place, unmounting this tab and every
  // staged row with it, so the host is told what is at stake and asks first.
  useEffect(() => {
    onUnsavedChangesChange?.(setup.dirtyCount);
    return () => onUnsavedChangesChange?.(0);
  }, [setup.dirtyCount, onUnsavedChangesChange]);

  // The first load only. A row Save reloads with the catalogue already in hand,
  // and swapping the screen for a spinner then would unmount the Delivery tab.
  if (setup.loading && !setup.catalogue) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading notification events…
        </CardContent>
      </Card>
    );
  }

  if (setup.error) {
    return (
      <Card>
        <CardContent className="space-y-4 py-12 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{setup.error}</p>
          <Button variant="outline" onClick={() => void setup.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Whose configuration this is, as the server resolved it — not as the top bar
  // or this component guessed it. Null only when the payload named no company.
  // Batched mail needs both switches, as the backend's channel_allowed('digest').
  const batchedMail = setup.master.digestEnabled && setup.master.emailsEnabled;
  // The one definition of what the one daily digest is doing: every line on this
  // screen about it reads these two, so no two can disagree.
  const digestDelivery = tenantOneDaily(
    setup.catalogue?.oneDailyDigest ?? false,
    batchedMail,
    setup.master.bellEnabled,
  );
  const digestActive = digestDelivery === 'digest';
  const scopeStatement = describeScope(
    setup.catalogue?.companyName ?? '',
    setup.catalogue?.scopeSource ?? '',
  );
  // CR-9029: the routing of that same company, said whichever way it is set.
  const ownerFirst = setup.catalogue?.baselineRouting ?? false;
  const routingLabel = ownerFirst ? ROUTING_LABELS.ownerFirst : ROUTING_LABELS.standard;
  const routingExplanation = ownerFirst
    ? ROUTING_EXPLANATIONS.ownerFirst
    : ROUTING_EXPLANATIONS.standard;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h2 className="text-xl font-semibold">Notifications</h2>
            {setup.catalogue && (
              <p className="text-sm text-muted-foreground">
                {setup.frequencySummary.total} events, {setup.catalogue.overriddenCount} customised
              </p>
            )}
          </div>
          {scopeStatement && (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              {scopeStatement}
            </p>
          )}
          {setup.catalogue && (
            <div className="mt-1" data-testid="notif-routing">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <Route className="h-4 w-4 shrink-0 text-muted-foreground" />
                Routing: {routingLabel}
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex text-muted-foreground hover:text-foreground"
                        aria-label={`About ${routingLabel} routing`}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" align="start" className="max-w-md">
                      {routingExplanation}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
              <p className="ml-[1.375rem] text-xs text-muted-foreground">{ROUTING_CHANGED_BY}</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {setup.dirtyCount > 0 && (
            <>
              <Badge variant="secondary">
                {setup.dirtyCount} unsaved change{setup.dirtyCount === 1 ? '' : 's'}
              </Badge>
              <Button variant="ghost" disabled={setup.saving} onClick={setup.discardChanges}>
                Discard
              </Button>
            </>
          )}
          <Button
            disabled={!setup.canConfigure || setup.dirtyCount === 0 || setup.saving}
            onClick={() => void setup.saveChanges()}
          >
            {setup.saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {!setup.canConfigure && (
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            Only company administrators can change notification configuration for this tenant.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList aria-label="Notification settings">
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="delivery">Delivery</TabsTrigger>
        </TabsList>
        <TabsContent value="events" className="space-y-4">
          <NotificationEventsTab
            setup={setup}
            view={eventsView}
            canSetInstallSwitches={canSetInstallSwitches}
            batchedMail={batchedMail}
            digestDelivery={digestDelivery}
          />
        </TabsContent>
        {/* Stays mounted through a tab switch and a row Save so its cards keep
            their unapplied edits; the key drops them on a change of company. A
            force-mounted panel is not hidden by Radix, hence the class. */}
        <TabsContent
          value="delivery"
          forceMount
          className="space-y-4 data-[state=inactive]:hidden"
        >
          <NotificationDeliveryTab
            key={setup.catalogue?.companyId ?? 'unscoped'}
            setup={setup}
            quietHours={quietHours}
            canSetInstallSwitches={canSetInstallSwitches}
            batchedMail={batchedMail}
            digestDelivery={digestDelivery}
            digestActive={digestActive}
            platformSettings={platformSettings}
            savingPlatform={saving}
            onPlatformSettingsChange={onPlatformSettingsChange}
            onSavePlatform={onSave}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
